import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list } from '../lib/api'
import type { Finding } from '../lib/api'
import { Empty, PageHead, Severity, Skel, Stat } from '../components/ui'
import { money, number, pct, riskTone, thaiDateTime } from '../lib/format'

/**
 * Adaptive fraud-risk learning (part 153).
 *
 * The page never computes a score or a weight: the server scores a finding with the stored model
 * and, on feedback, applies one fixed learning step and returns the old→new pair for every weight.
 * What is shown here is exactly what was persisted, which is what an auditor has to be able to
 * replay.
 */

interface Weight {
  feature_key: string
  feature_label: string
  value: number
  version: number
}

interface Model {
  id: string
  code: string
  name_th: string
  version: number
  learning_rate: number
  bias: number
  weight_min: number
  weight_max: number
  weights: Weight[]
  feedback_count: number
  last_trained_at?: string
  created_at: string
  updated_at: string
}

interface ScoreFeature {
  key: string
  label: string
  value: number
  value_label: string
  weight: number
  contribution: number
  detail: string
}

interface Score {
  finding_id: string
  finding_title: string
  rule_code: string
  model_id: string
  model_version: number
  risk: number
  band: 'low' | 'elevated' | 'high' | 'critical'
  band_label: string
  probability: number
  bias: number
  features: ScoreFeature[]
  explanation: string
  learning_rate: number
}

interface Change {
  feature_key: string
  feature_label: string
  old_value: number
  new_value: number
  delta: number
}

interface FeedbackResult {
  id: string
  model_id: string
  finding_id: string
  outcome: 'confirmed_fraud' | 'rejected'
  outcome_label: string
  note: string
  score_before: number
  score_after: number
  score_delta: number
  version_before: number
  version_after: number
  bias_before: number
  bias_after: number
  changes: Change[]
  created_at: string
}

interface WeightRow {
  id: string
  model_id: string
  feature_key: string
  feature_label: string
  version: number
  old_value: number
  new_value: number
  delta: number
  feedback_id: string
  outcome: string
  outcome_label: string
  finding_id: string
  created_at: string
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function signed(value: number): string {
  return `${value > 0 ? '+' : ''}${value.toFixed(4)}`
}

export function AdaptiveRisk() {
  const qc = useQueryClient()
  const [modelId, setModelId] = useState<string | null>(null)
  const [findingId, setFindingId] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [result, setResult] = useState<FeedbackResult | null>(null)

  const models = useQuery({
    queryKey: ['adaptive-models'],
    queryFn: () => list<Model>('/v1/adaptive/models'),
    staleTime: 15_000,
  })
  const modelRows = models.data ?? []
  const activeModel = modelRows.find((m) => m.id === modelId) ?? modelRows[0] ?? null

  const findings = useQuery({
    queryKey: ['adaptive-findings'],
    queryFn: () => list<Finding>('/v1/findings?limit=200'),
    staleTime: 60_000,
  })
  const findingRows = findings.data ?? []
  const activeFinding = findingRows.find((f) => f.id === findingId) ?? findingRows[0] ?? null

  const score = useQuery({
    queryKey: ['adaptive-score', activeModel?.id, activeFinding?.id],
    queryFn: () => api<Score>(`/v1/adaptive/findings/${activeFinding?.id}/score?model_id=${activeModel?.id}`),
    enabled: Boolean(activeModel && activeFinding),
    staleTime: 10_000,
  })

  const weights = useQuery({
    queryKey: ['adaptive-weights', activeModel?.id],
    queryFn: () => list<WeightRow>(`/v1/adaptive/models/${activeModel?.id}/weights?limit=300`),
    enabled: Boolean(activeModel),
    staleTime: 10_000,
  })

  const createModel = useMutation({
    mutationFn: () => apiWrite<Model>('/v1/adaptive/models', 'POST', {}),
    onSuccess: (created) => {
      setModelId(created.id)
      setResult(null)
      void qc.invalidateQueries({ queryKey: ['adaptive-models'] })
    },
  })

  const submit = useMutation({
    mutationFn: (outcome: 'confirmed_fraud' | 'rejected') =>
      apiWrite<FeedbackResult>('/v1/adaptive/feedback', 'POST', {
        model_id: activeModel?.id,
        finding_id: activeFinding?.id,
        outcome,
        note,
      }),
    onSuccess: (feedback) => {
      setResult(feedback)
      setNote('')
      void qc.invalidateQueries({ queryKey: ['adaptive-models'] })
      void qc.invalidateQueries({ queryKey: ['adaptive-weights'] })
      void qc.invalidateQueries({ queryKey: ['adaptive-score'] })
    },
  })

  const historyRows = weights.data ?? []
  const latestScore = score.data?.risk ?? 0

  return (
    <>
      <PageHead
        title="ความเสี่ยงแบบปรับเรียนรู้"
        sub="โมเดลให้คะแนนข้อค้นพบจากน้ำหนักที่เรียนรู้จากผลตรวจสอบยืนยัน · ทุกการปรับน้ำหนักบันทึกค่าเดิมและค่าใหม่เพื่อตรวจสอบย้อนกลับ"
        actions={
          <div className="row" style={{ gap: 8 }}>
            {modelRows.length > 0 ? (
              <select
                className="select"
                aria-label="เลือกโมเดลความเสี่ยง"
                value={activeModel?.id ?? ''}
                onChange={(e) => { setModelId(e.target.value); setResult(null) }}
                style={{ minWidth: 200 }}
              >
                {modelRows.map((m) => (
                  <option key={m.id} value={m.id}>{m.name_th} · v{m.version}</option>
                ))}
              </select>
            ) : null}
            {modelRows.length === 0 ? (
              <button
                className="btn btn-primary btn-sm"
                disabled={createModel.isPending || models.isLoading}
                onClick={() => createModel.mutate()}
              >
                {createModel.isPending ? 'กำลังสร้าง…' : 'สร้างโมเดลเริ่มต้น'}
              </button>
            ) : null}
          </div>
        }
      />

      {createModel.isError ? (
        <div className="form-error" style={{ marginBottom: 16 }}>
          {errorText(createModel.error, 'สร้างโมเดลไม่สำเร็จ')}
        </div>
      ) : null}

      {modelRows.length === 0 && !models.isLoading ? (
        <div className="card card-pad">
          <Empty
            title="ยังไม่มีโมเดลความเสี่ยง"
            body="สร้างโมเดลเริ่มต้นเพื่อให้น้ำหนักสี่ปัจจัยเริ่มต้นที่ค่าเดียวกันทุกองค์กร แล้วจึงให้คะแนนข้อค้นพบและส่งผลตรวจสอบเพื่อให้น้ำหนักปรับตามจริง"
          />
        </div>
      ) : null}

      <div className="grid-4 stagger" style={{ marginTop: 4 }}>
        <Stat
          label="เวอร์ชันโมเดล"
          value={activeModel ? `v${activeModel.version}` : '—'}
          foot={activeModel ? `อัตราการเรียนรู้ ${activeModel.learning_rate}` : 'ยังไม่ได้สร้างโมเดล'}
        />
        <Stat
          label="ผลตรวจสอบที่เรียนรู้แล้ว"
          value={activeModel ? number(activeModel.feedback_count) : '—'}
          unit="ครั้ง"
          foot={activeModel?.last_trained_at ? `ล่าสุด ${thaiDateTime(activeModel.last_trained_at)}` : 'ยังไม่มีการเรียนรู้'}
        />
        <Stat
          label="ค่าคงที่ของโมเดล (bias)"
          value={activeModel ? activeModel.bias.toFixed(3) : '—'}
          foot="ปรับทีละขั้นตามผลตรวจสอบ ไม่เกินขอบเขตที่กำหนด"
        />
        <Stat
          label="คะแนนข้อค้นพบที่เลือก"
          value={score.data ? number(latestScore) : '—'}
          unit={score.data ? '/ 100' : undefined}
          foot={score.data ? `เวอร์ชัน v${score.data.model_version}` : 'เลือกข้อค้นพบเพื่อให้คะแนน'}
        />
      </div>

      {models.isLoading ? (
        <div className="stack" style={{ marginTop: 16 }}><Skel h={220} /><Skel h={180} /></div>
      ) : modelRows.length > 0 ? (
        <div className="grid-main" style={{ marginTop: 16 }}>
          <div className="card card-pad">
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span className="card-label">ข้อค้นพบที่รอให้คะแนน</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                เลือกแถวแล้วส่งผลตรวจสอบเพื่อปรับน้ำหนัก
              </span>
            </div>
            {findings.isLoading ? (
              <div style={{ marginTop: 12 }}><Skel h={220} /></div>
            ) : findings.isError ? (
              <div className="form-error" style={{ marginTop: 12 }}>
                {errorText(findings.error, 'โหลดข้อค้นพบไม่สำเร็จ')}
              </div>
            ) : findingRows.length === 0 ? (
              <Empty title="ยังไม่มีข้อค้นพบ" body="ข้อค้นพบจะปรากฏที่นี่เมื่อกฎตรวจสอบทำงาน" />
            ) : (
              <div className="table-wrap" style={{ marginTop: 12, maxHeight: 430, overflowY: 'auto' }}>
                <table className="table data">
                  <thead>
                    <tr>
                      <th>ข้อค้นพบ</th>
                      <th>ความรุนแรง</th>
                      <th style={{ textAlign: 'right' }}>คะแนนกฎ</th>
                      <th style={{ textAlign: 'right' }}>มูลค่าที่เสี่ยง</th>
                    </tr>
                  </thead>
                  <tbody>
                    {findingRows.map((finding) => (
                      <tr
                        key={finding.id}
                        onClick={() => { setFindingId(finding.id); setResult(null) }}
                        style={{
                          cursor: 'pointer',
                          background: finding.id === activeFinding?.id ? 'rgba(200, 169, 106, 0.06)' : undefined,
                        }}
                      >
                        <td>
                          <div>{finding.title}</div>
                          <div className="dim" style={{ fontSize: 11 }}>{finding.rule_code}</div>
                        </td>
                        <td><Severity value={finding.severity} /></td>
                        <td className="tnum" style={{ textAlign: 'right' }}>{finding.risk_score}</td>
                        <td className="tnum" style={{ textAlign: 'right', fontSize: 11.5 }}>
                          {finding.amount_at_risk_satang > 0 ? money(finding.amount_at_risk_satang, { compact: true }) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card card-pad">
            {!activeFinding ? (
              <Empty title="ยังไม่ได้เลือกข้อค้นพบ" body="เลือกข้อค้นพบจากตารางเพื่อดูคะแนนที่โมเดลให้และส่งผลตรวจสอบ" />
            ) : score.isLoading ? (
              <Skel h={300} />
            ) : score.isError ? (
              <div className="form-error">{errorText(score.error, 'ให้คะแนนไม่สำเร็จ')}</div>
            ) : score.data ? (
              <>
                <div className="card-label">คะแนนจากโมเดลปัจจุบัน</div>
                <div className="row" style={{ alignItems: 'baseline', marginTop: 10 }}>
                  <span className={`tnum ${riskTone(score.data.risk)}`} style={{ fontSize: 34, fontWeight: 300 }}>
                    {score.data.risk}
                  </span>
                  <span className="dim" style={{ fontSize: 12 }}>/ 100</span>
                  <span className={`sev ${riskTone(score.data.risk)}`} style={{ marginLeft: 8 }}>
                    <i className="sev-dot" />
                    {score.data.band_label}
                  </span>
                </div>
                <div style={{ fontSize: 12.5, marginTop: 8 }}>{score.data.explanation}</div>
                <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                  {score.data.finding_title} · {score.data.rule_code} · เทียบกับความน่าจะเป็นการทุจริต{' '}
                  {pct(score.data.probability)} · bias {score.data.bias.toFixed(3)}
                </div>

                <div className="table-wrap" style={{ marginTop: 14 }}>
                  <table className="table data">
                    <thead>
                      <tr>
                        <th>ปัจจัย</th>
                        <th style={{ textAlign: 'right' }}>ค่า (0–1)</th>
                        <th style={{ textAlign: 'right' }}>น้ำหนัก</th>
                        <th style={{ textAlign: 'right' }}>ส่วนร่วม</th>
                      </tr>
                    </thead>
                    <tbody>
                      {score.data.features.map((feature) => (
                        <tr key={feature.key}>
                          <td>
                            <div>{feature.label}</div>
                            <div className="dim" style={{ fontSize: 10.5 }}>{feature.detail}</div>
                          </td>
                          <td className="tnum" style={{ textAlign: 'right' }}>
                            {feature.value.toFixed(3)}
                            <div className="dim" style={{ fontSize: 10.5 }}>{feature.value_label}</div>
                          </td>
                          <td className="tnum" style={{ textAlign: 'right' }}>{feature.weight.toFixed(3)}</td>
                          <td className="tnum" style={{ textAlign: 'right' }}>
                            {feature.contribution >= 0 ? '+' : ''}{feature.contribution.toFixed(4)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ borderTop: '1px solid var(--color-hairline)', marginTop: 16, paddingTop: 14 }}>
                  <div className="card-label">ส่งผลตรวจสอบเพื่อให้น้ำหนักปรับ</div>
                  <div className="field">
                    <label htmlFor="adaptive-note">หมายเหตุ (ไม่บังคับ)</label>
                    <textarea
                      id="adaptive-note"
                      className="input"
                      value={note}
                      maxLength={500}
                      rows={2}
                      placeholder="เหตุผลประกอบผลตรวจสอบ เช่น ยืนยันกับเอกสารต้นฉบับแล้ว"
                      onChange={(e) => setNote(e.target.value)}
                      style={{ width: '100%', height: 'auto', paddingTop: 8, paddingBottom: 8 }}
                    />
                  </div>
                  <div className="row" style={{ gap: 8, marginTop: 10 }}>
                    <button
                      className="btn btn-primary btn-sm"
                      disabled={submit.isPending}
                      onClick={() => submit.mutate('confirmed_fraud')}
                    >
                      {submit.isPending ? 'กำลังบันทึก…' : 'ยืนยันการทุจริต'}
                    </button>
                    <button
                      className="btn btn-sm"
                      disabled={submit.isPending}
                      onClick={() => submit.mutate('rejected')}
                    >
                      ไม่ใช่การทุจริต
                    </button>
                  </div>
                  <div className="dim" style={{ fontSize: 11, marginTop: 8 }}>
                    หนึ่งข้อค้นพบฝึกได้ครั้งเดียวต่อโมเดล · ค่าที่ส่งจะมีผลกับคะแนนของข้อค้นพบอื่นในองค์กรด้วย
                  </div>
                  {submit.isError ? (
                    <div className="form-error">{errorText(submit.error, 'บันทึกผลตรวจสอบไม่สำเร็จ')}</div>
                  ) : null}
                </div>
              </>
            ) : (
              <Empty title="ยังไม่มีคะแนน" body="เลือกข้อค้นพบเพื่อให้โมเดลให้คะแนน" />
            )}
          </div>
        </div>
      ) : null}

      {result ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <div className="row" style={{ alignItems: 'baseline' }}>
            <span className="card-label">ผลการเรียนรู้ที่บันทึกแล้ว</span>
            <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
              {result.outcome_label} · {thaiDateTime(result.created_at)}
            </span>
          </div>
          <div className="row" style={{ alignItems: 'baseline', marginTop: 10, gap: 10 }}>
            <span className="tnum" style={{ fontSize: 26, fontWeight: 300 }}>{result.score_before}</span>
            <span className="dim">→</span>
            <span className={`tnum ${riskTone(result.score_after)}`} style={{ fontSize: 26, fontWeight: 300 }}>
              {result.score_after}
            </span>
            <span className={`sev ${result.score_delta >= 0 ? 'sev-high' : 'sev-low'}`}>
              <i className="sev-dot" />
              {result.score_delta >= 0 ? '+' : ''}{result.score_delta} คะแนน
            </span>
            <span className="dim" style={{ fontSize: 11.5 }}>
              เวอร์ชัน {result.version_before} → {result.version_after} · bias {result.bias_before.toFixed(3)} → {result.bias_after.toFixed(3)}
            </span>
          </div>
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="table data">
              <thead>
                <tr>
                  <th>ปัจจัย</th>
                  <th style={{ textAlign: 'right' }}>น้ำหนักเดิม</th>
                  <th style={{ textAlign: 'right' }}>น้ำหนักใหม่</th>
                  <th style={{ textAlign: 'right' }}>ผลต่าง</th>
                </tr>
              </thead>
              <tbody>
                {result.changes.map((change) => (
                  <tr key={change.feature_key}>
                    <td>{change.feature_label}</td>
                    <td className="tnum" style={{ textAlign: 'right' }}>{change.old_value.toFixed(6)}</td>
                    <td className="tnum" style={{ textAlign: 'right' }}>{change.new_value.toFixed(6)}</td>
                    <td className={`tnum ${change.delta > 0 ? 'sev-high' : change.delta < 0 ? 'sev-low' : 'dim'}`} style={{ textAlign: 'right' }}>
                      {signed(change.delta)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {activeModel ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <div className="row" style={{ alignItems: 'baseline' }}>
            <span className="card-label">ประวัติน้ำหนัก (ค่าเดิม → ค่าใหม่)</span>
            <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
              บันทึกแบบเพิ่มอย่างเดียว {number(historyRows.length)} รายการ · ไม่มีการแก้ไขย้อนหลัง
            </span>
          </div>
          {weights.isLoading ? (
            <div style={{ marginTop: 12 }}><Skel h={220} /></div>
          ) : weights.isError ? (
            <div className="form-error" style={{ marginTop: 12 }}>
              {errorText(weights.error, 'โหลดประวัติน้ำหนักไม่สำเร็จ')}
            </div>
          ) : historyRows.length === 0 ? (
            <Empty title="ยังไม่มีประวัติน้ำหนัก" body="เมื่อส่งผลตรวจสอบครั้งแรก ระบบจะบันทึกน้ำหนักเดิมและใหม่ของทุกปัจจัย" />
          ) : (
            <div className="table-wrap" style={{ marginTop: 12, maxHeight: 420, overflowY: 'auto' }}>
              <table className="table data">
                <thead>
                  <tr>
                    <th>เวอร์ชัน</th>
                    <th>ปัจจัย</th>
                    <th style={{ textAlign: 'right' }}>ค่าเดิม</th>
                    <th style={{ textAlign: 'right' }}>ค่าใหม่</th>
                    <th style={{ textAlign: 'right' }}>ผลต่าง</th>
                    <th>ผลตรวจสอบ</th>
                    <th style={{ textAlign: 'right' }}>เวลา</th>
                  </tr>
                </thead>
                <tbody>
                  {historyRows.map((row) => (
                    <tr
                      key={row.id}
                      style={{ background: result && row.feedback_id === result.id ? 'rgba(200, 169, 106, 0.06)' : undefined }}
                    >
                      <td className="tnum">v{row.version}</td>
                      <td>{row.feature_label}</td>
                      <td className="tnum" style={{ textAlign: 'right' }}>{row.old_value.toFixed(6)}</td>
                      <td className="tnum" style={{ textAlign: 'right' }}>{row.new_value.toFixed(6)}</td>
                      <td className={`tnum ${row.delta > 0 ? 'sev-high' : row.delta < 0 ? 'sev-low' : 'dim'}`} style={{ textAlign: 'right' }}>
                        {signed(row.delta)}
                      </td>
                      <td className="dim">
                        {row.outcome_label || 'ค่าเริ่มต้น'}
                        {row.finding_id ? <div style={{ fontSize: 10.5 }}>{row.finding_id.slice(0, 8)}</div> : null}
                      </td>
                      <td className="dim tnum" style={{ textAlign: 'right', fontSize: 11 }}>{thaiDateTime(row.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="dim" style={{ fontSize: 11, marginTop: 10 }}>
            ขั้นการเรียนรู้คงที่: อัตราการเรียนรู้ × (ผลจริง − ความน่าจะเป็น) × ค่าปัจจัย · น้ำหนักถูกจำกัดช่วง
            {activeModel ? ` ${activeModel.weight_min} ถึง ${activeModel.weight_max}` : ''} และเวอร์ชันเพิ่มหนึ่งทุกครั้ง
          </div>
        </div>
      ) : null}
    </>
  )
}
